import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  BookOpen, Plus, FileText, MessageSquare, Trash2, Loader2,
  Search, Sparkles, Upload, AlertCircle,
} from 'lucide-react';

/**
 * Knowledge Base page. Customers manage the Q&A pairs, free-form notes and
 * uploaded PDFs that Ava can quote during a live call (the convo_lookup_kb
 * node performs RAG against this corpus).
 */
export default function KnowledgeBasePage() {
  const { api } = useAuth();
  const [sources, setSources] = useState([]);
  const [counts, setCounts] = useState({ qa: 0, note: 0, pdf: 0, chunks: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('qa');
  const [busy, setBusy] = useState(false);
  const [busyDelete, setBusyDelete] = useState('');
  const [noBusiness, setNoBusiness] = useState(false);
  // Test panel
  const [testQ, setTestQ] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get('/knowledge');
      setSources(data?.sources || []);
      setCounts(data?.counts || { qa: 0, note: 0, pdf: 0, chunks: 0 });
    } catch (e) {
      // 400 "No business associated" surfaces while the customer is still
      // mid-onboarding. Show an inline empty-state instead of a noisy toast.
      const detail = e.response?.data?.detail;
      if (e.response?.status === 400 && detail && /business/i.test(detail)) {
        setNoBusiness(true);
      } else {
        toast.error(detail || 'Could not load your knowledge base.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const removeSource = async (id) => {
    if (!window.confirm('Remove this entry from Ava\'s knowledge base?')) return;
    setBusyDelete(id);
    try {
      await api.delete(`/knowledge/${id}`);
      toast.success('Removed.');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove.');
    } finally {
      setBusyDelete('');
    }
  };

  const runTest = async () => {
    const q = (testQ || '').trim();
    if (q.length < 3) { toast.error('Type a longer question to test.'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/knowledge/search', { query: q, top_k: 3 });
      setTestResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Search failed.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div data-testid="knowledge-base-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      {noBusiness ? (
        <div className="max-w-xl mx-auto mt-12 text-center bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] rounded-xl p-8">
          <BookOpen className="w-10 h-10 text-blue-500 mx-auto mb-3" />
          <h2 className="text-lg font-heading font-semibold text-gray-900 dark:text-white mb-1.5">
            Finish onboarding first
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Your business profile needs to be set up before you can build a knowledge base for Ava. Complete onboarding and head back here to start adding Q&amp;A pairs and PDFs.
          </p>
        </div>
      ) : (<>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-5 h-5 text-blue-500" />
            <h1 className="text-xl font-heading font-semibold text-gray-900 dark:text-white">Knowledge Base</h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-slate-400 max-w-xl">
            Anything you add here is searched in real time when Ava hits a <span className="font-medium text-gray-700 dark:text-slate-200">Lookup KB</span> node on a live call. Add Q&amp;A pairs, paste notes, or upload PDFs of your menu, services, pricing, FAQs.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <Stat label="Q&A" value={counts.qa} />
          <Stat label="Notes" value={counts.note} />
          <Stat label="PDFs" value={counts.pdf} />
          <Stat label="Chunks" value={counts.chunks} accent />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-3 border-b border-gray-200 dark:border-white/[0.06]">
        {[
          { id: 'qa',   label: 'Q&A',         icon: MessageSquare },
          { id: 'note', label: 'Note',        icon: FileText },
          { id: 'pdf',  label: 'Upload PDF',  icon: Upload },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`kb-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 text-sm px-3.5 py-2 -mb-px border-b-2 transition-colors ${tab === t.id
              ? 'border-blue-500 text-blue-600 dark:text-blue-300'
              : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white'}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Add panel */}
        <div className="lg:col-span-1 space-y-3">
          <div className="bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] rounded-xl p-4">
            {tab === 'qa' && <AddQaForm api={api} onAdded={refresh} setBusy={setBusy} busy={busy} />}
            {tab === 'note' && <AddNoteForm api={api} onAdded={refresh} setBusy={setBusy} busy={busy} />}
            {tab === 'pdf' && <AddPdfForm api={api} onAdded={refresh} setBusy={setBusy} busy={busy} />}
          </div>

          {/* Test Ava panel */}
          <div className="bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Test what Ava would say</h3>
            </div>
            <p className="text-[12px] text-gray-500 dark:text-slate-400 mb-3">
              Ask a question the way a customer would. We&apos;ll show the answer Ava would read on the call.
            </p>
            <div className="flex gap-2">
              <input
                data-testid="kb-test-input"
                value={testQ}
                onChange={(e) => setTestQ(e.target.value)}
                placeholder="What time do you close?"
                className="flex-1 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] focus:border-purple-400 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none"
                onKeyDown={(e) => { if (e.key === 'Enter') runTest(); }}
              />
              <button
                data-testid="kb-test-btn"
                onClick={runTest}
                disabled={testing}
                className="text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg px-3 inline-flex items-center gap-1.5"
              >
                {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              </button>
            </div>
            {testResult && (
              <div data-testid="kb-test-result" className="mt-3 space-y-2">
                {testResult.spoken ? (
                  <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-3">
                    <p className="text-[11px] uppercase tracking-wide font-medium text-blue-700 dark:text-blue-300 mb-1">Ava would say</p>
                    <p className="text-[13px] text-gray-800 dark:text-slate-100 leading-relaxed">{testResult.spoken}</p>
                  </div>
                ) : (
                  <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[13px] text-gray-700 dark:text-slate-200">
                      Ava is not confident. She would offer to take a message instead. Add more detail so she can answer this confidently.
                    </p>
                  </div>
                )}
                {(testResult.hits || []).length > 0 && (
                  <details className="text-[12px] text-gray-500 dark:text-slate-400">
                    <summary className="cursor-pointer hover:text-gray-700 dark:hover:text-slate-200">
                      Top matches ({testResult.hits.length})
                    </summary>
                    <div className="mt-1.5 space-y-1.5">
                      {testResult.hits.map((h, i) => (
                        <div key={h.id || i} className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-md px-2.5 py-1.5">
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <span className="text-[11px] font-medium text-gray-700 dark:text-slate-200 truncate">{h.title}</span>
                            <span className="text-[10px] tabular-nums text-gray-400 dark:text-slate-500">{(h.score * 100).toFixed(0)}%</span>
                          </div>
                          <p className="text-[11.5px] text-gray-600 dark:text-slate-300 line-clamp-2">{h.content}</p>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sources list */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] rounded-xl">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-white/[0.06] flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Your knowledge ({sources.length})</h3>
            </div>
            {loading ? (
              <div className="px-4 py-12 flex justify-center">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            ) : sources.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <BookOpen className="w-8 h-8 text-gray-300 dark:text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  No knowledge yet. Add your first Q&amp;A on the left so Ava can answer caller questions confidently.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                {sources.map((s) => (
                  <li key={s.id} className="px-4 py-3 flex items-start gap-3" data-testid={`kb-source-${s.id}`}>
                    <SourceIcon kind={s.kind} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>
                      <p className="text-[11.5px] text-gray-500 dark:text-slate-400 mt-0.5">
                        {s.kind === 'qa' && 'Q&A pair'}
                        {s.kind === 'note' && `Note, ${s.chunks} ${s.chunks === 1 ? 'chunk' : 'chunks'}`}
                        {s.kind === 'pdf' && `PDF, ${s.chunks} ${s.chunks === 1 ? 'chunk' : 'chunks'}${s.size_bytes ? `, ${Math.round(s.size_bytes / 1024)} KB` : ''}`}
                        {' '}· {new Date(s.created_at).toLocaleDateString()}
                      </p>
                      {s.kind === 'qa' && s.answer && (
                        <p className="text-[12px] text-gray-600 dark:text-slate-300 mt-1 line-clamp-2">{s.answer}</p>
                      )}
                    </div>
                    <button
                      data-testid={`kb-remove-${s.id}`}
                      onClick={() => removeSource(s.id)}
                      disabled={busyDelete === s.id}
                      className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10"
                    >
                      {busyDelete === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`px-2.5 py-1.5 rounded-lg ${accent ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/20' : 'bg-gray-50 dark:bg-white/[0.04] text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-white/[0.06]'}`}>
      <span className="font-semibold tabular-nums">{value}</span>
      <span className="text-[10px] uppercase tracking-wide ml-1 opacity-75">{label}</span>
    </div>
  );
}

function SourceIcon({ kind }) {
  if (kind === 'qa')   return <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-500/15 text-blue-600 dark:text-blue-300 flex items-center justify-center flex-shrink-0"><MessageSquare className="w-3.5 h-3.5" /></div>;
  if (kind === 'note') return <div className="w-7 h-7 rounded-lg bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300 flex items-center justify-center flex-shrink-0"><FileText className="w-3.5 h-3.5" /></div>;
  if (kind === 'pdf')  return <div className="w-7 h-7 rounded-lg bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-300 flex items-center justify-center flex-shrink-0"><FileText className="w-3.5 h-3.5" /></div>;
  return null;
}

function AddQaForm({ api, onAdded, busy, setBusy }) {
  const [q, setQ] = useState('');
  const [a, setA] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!q.trim() || !a.trim()) { toast.error('Both fields are required.'); return; }
    setBusy(true);
    try {
      await api.post('/knowledge/qa', { question: q.trim(), answer: a.trim() });
      toast.success('Q&A added. Ava can quote this on calls.');
      setQ(''); setA('');
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add Q&A.');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
        <MessageSquare className="w-4 h-4 text-blue-500" /> Add a Q&amp;A pair
      </h3>
      <input data-testid="kb-qa-question" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Question (what would a caller ask?)"
        className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] focus:border-blue-500 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none" />
      <textarea data-testid="kb-qa-answer" value={a} onChange={(e) => setA(e.target.value)} rows={4} placeholder="Answer Ava should give"
        className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] focus:border-blue-500 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none resize-none" />
      <button data-testid="kb-qa-submit" disabled={busy} type="submit"
        className="w-full text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg px-3 py-2 inline-flex items-center justify-center gap-1.5">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        Add to knowledge base
      </button>
    </form>
  );
}

function AddNoteForm({ api, onAdded, busy, setBusy }) {
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 10) { toast.error('Add at least a sentence or two.'); return; }
    setBusy(true);
    try {
      await api.post('/knowledge/note', { name: name.trim() || undefined, body: body.trim() });
      toast.success('Note saved.');
      setName(''); setBody('');
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save note.');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
        <FileText className="w-4 h-4 text-amber-500" /> Paste a note
      </h3>
      <input data-testid="kb-note-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Title (optional, e.g. Cancellation Policy)"
        className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] focus:border-blue-500 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none" />
      <textarea data-testid="kb-note-body" value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Paste any text. Pricing, services, hours, anything you want Ava to know."
        className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] focus:border-blue-500 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none resize-none" />
      <button data-testid="kb-note-submit" disabled={busy} type="submit"
        className="w-full text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg px-3 py-2 inline-flex items-center justify-center gap-1.5">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        Save note
      </button>
    </form>
  );
}

function AddPdfForm({ api, onAdded, busy, setBusy }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Choose a PDF.'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('PDF is too large (limit 10 MB).'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      await api.post('/knowledge/pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('PDF processed and added.');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not process PDF.');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
        <Upload className="w-4 h-4 text-red-500" /> Upload a PDF
      </h3>
      <p className="text-[11.5px] text-gray-500 dark:text-slate-400">
        Menu, services list, FAQ, anything. Up to 10 MB. Image-only PDFs are not supported.
      </p>
      <input ref={fileRef} data-testid="kb-pdf-file" type="file" accept="application/pdf"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="block w-full text-sm text-gray-700 dark:text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 dark:file:bg-white/[0.06] file:text-gray-700 dark:file:text-slate-200 hover:file:bg-gray-200 dark:hover:file:bg-white/[0.10]" />
      <button data-testid="kb-pdf-submit" disabled={busy || !file} type="submit"
        className="w-full text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg px-3 py-2 inline-flex items-center justify-center gap-1.5">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        Upload &amp; index
      </button>
    </form>
  );
}
